import { IPositionType } from 'src/generated-types'
import { IOlexMatchPreview, IOlexMatchStatus, IOlexNameIssue } from 'src/generated-types/olex-match'
import { getCageGenericIdentification } from 'src/utils/function'

export const OLEX_ACCEPTED_TYPES = ['application/gzip', 'application/x-gzip']

// An Olex file already matched to the draft, handed to the Olex mode together with its preview.
export interface OlexRequest {
  file: File
  preview: IOlexMatchPreview
}

export interface OlexMatchChoices {
  useOlexNames: boolean
  // Existing line id to the index of the file line it is moved to by hand.
  manualMoves: Record<number, number>
}

export const DEFAULT_OLEX_CHOICES: OlexMatchChoices = { useOlexNames: false, manualMoves: {} }

export const olexFormData = (file: File, choices: OlexMatchChoices = DEFAULT_OLEX_CHOICES) => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('useOlexNames', String(choices.useOlexNames))
  Object.entries(choices.manualMoves).forEach(([lineId, fileLine]) => {
    formData.append('movedLineIds', lineId)
    formData.append('movedToFileLines', String(fileLine))
  })
  return formData
}

// The hand moves after moving one line to a file line, or (undefined) letting it be removed.
export const withManualMove = (moves: Record<number, number>, lineId: number, fileLine?: number) => {
  const rest = Object.fromEntries(Object.entries(moves).filter(([id]) => Number(id) !== lineId))
  return fileLine == null ? rest : { ...rest, [lineId]: fileLine }
}

// What to pick out on the map while the user points at a line: where it is now (the faded draft) and where the upload
// puts it (the preview).
export interface OlexMapHighlight {
  currentIds: number[]
  previewIds: number[]
}

export const lineHighlight = (lineId: number): OlexMapHighlight => ({ currentIds: [lineId], previewIds: [lineId] })

export const previewHighlight = (positionId?: number | null): OlexMapHighlight => ({
  currentIds: [],
  previewIds: positionId == null ? [] : [positionId],
})

export const NAME_ISSUE_KEY: Partial<Record<IOlexNameIssue, string>> = {
  [IOlexNameIssue.Taken]: 'revision-olex-name-taken',
  [IOlexNameIssue.Reused]: 'revision-olex-name-reused',
  [IOlexNameIssue.WrongType]: 'revision-olex-name-wrong-type',
}

const STATUS_MESSAGE_KEY: Partial<Record<IOlexMatchStatus, string>> = {
  [IOlexMatchStatus.NotADraft]: 'revision-olex-not-a-draft',
  [IOlexMatchStatus.NotSupported]: 'revision-olex-unsupported',
  [IOlexMatchStatus.NotPlaced]: 'revision-olex-not-placed',
  [IOlexMatchStatus.NoCoordinates]: 'revision-olex-no-coordinates',
  [IOlexMatchStatus.InvalidFile]: 'revision-olex-invalid-file',
  [IOlexMatchStatus.NoOverlap]: 'revision-olex-no-overlap',
  [IOlexMatchStatus.OffGrid]: 'revision-olex-off-grid',
  [IOlexMatchStatus.NamesExhausted]: 'revision-olex-names-exhausted',
}

export const olexMatchStatusKey = (status: IOlexMatchStatus) => STATUS_MESSAGE_KEY[status]

// A layout the draft's grid cannot be matched to (a new layout, a barge, a service row) goes through the project's
// regular Olex upload, which builds the positions from scratch.
export const usesProjectOlexUpload = (status: IOlexMatchStatus) =>
  [
    IOlexMatchStatus.NoOverlap,
    IOlexMatchStatus.NotSupported,
    IOlexMatchStatus.NotPlaced,
    IOlexMatchStatus.NotADraft,
  ].includes(status)

export const OLEX_MATCH_TYPES: { type: IPositionType; key: string }[] = [
  { type: IPositionType.Corner, key: 'revision-expand-stat-corners' },
  { type: IPositionType.FrameLines, key: 'revision-expand-stat-frame-lines' },
  { type: IPositionType.Cage, key: 'revision-expand-stat-cages' },
  { type: IPositionType.Bridle, key: 'revision-expand-stat-bridles' },
  { type: IPositionType.MooringLine, key: 'revision-expand-stat-mooring-lines' },
]

export const isOlexNameUsable = (issue: IOlexNameIssue) =>
  issue === IOlexNameIssue.None || issue === IOlexNameIssue.Reused

// Cages are known by their letter.
export const olexNameLabel = (type: IPositionType, name: string) =>
  type === IPositionType.Cage ? getCageGenericIdentification(name) ?? name : name

export const hasOlexChanges = (preview: IOlexMatchPreview) =>
  preview.groups.some(g => g.added.length > 0 || g.removed.length > 0) ||
  (preview.useOlexNames && preview.names.some(n => isOlexNameUsable(n.issue)))

// The first names and how many more there are, so long lists stay on one line.
export const shortNames = (names: string[], max = 8) => ({
  shown: names.slice(0, max).join(', '),
  more: Math.max(0, names.length - max),
})
